import { Localized } from '@fluent/react';
import { CWUser } from 'cw-isdk/core';
import React, { useEffect, useState } from 'react';
import { catchError, of, tap } from 'rxjs';
import Container from 'typedi';
import { useRefStateObject } from '../../global-hooks/use-ref-state-object.hook';
import { useSymbol } from '../../global-hooks/use-symbol.hook';
import { LoggingService } from '../../logging.service';
import { Urls } from '../../urls';
import { LogInData, LoginError } from './log-in.data';

const logger = Container.get(LoggingService);

export function useLogIn(onLoggedIn: (user: CWUser) => void, onMfaRequired: (mfaToken: string) => void): UseLogInState {

    const [state, setState] = useRefStateObject<UseLogInState>({
        errorMessage: undefined,
        loading: false,
        logIn: (userName: string, password: string) => {
            setUserName(userName);
            setPassword(password);
            nextDoLogIn();
        }
    });

    const [userName, setUserName] = useState<string | undefined>();
    const [password, setPassword] = useState<string | undefined>();
    const [doLogIn, nextDoLogIn] = useSymbol();

    useEffect(() => {

        if (doLogIn == null) { return; }
        if (userName == null || userName.length < 1) {
            return;
        }
        if (password == null || password.length < 1) {
            return;
        }

        const loginData = Container.get(LogInData);
        setState({ errorMessage: undefined, loading: true });
        const subscription = loginData.credentialLogin(userName, password).pipe(
            tap(loginResponse => {
                setState({ loading: false });

                if (loginResponse.Error != null) {
                    let errorMessage: React.ReactElement | undefined;
                    switch (loginResponse.Error) {
                        case LoginError.RequiresReset:
                            errorMessage = (
                                <div>
                                    <Localized id='login-error-password-reset-required'>
                                        Your password must be reset before you can log in.
                                    </Localized>
                                    {' '}
                                    <Localized id='login-error-reset-password-link'>
                                        <a href={Urls.passwordResetPage}>Reset password</a>
                                    </Localized>
                                </div>);
                            break;
                        case LoginError.InvalidUserNameOrPassword:
                            errorMessage = (
                                <div>
                                    <Localized id='login-error-invalid-credentials'>
                                        Password/Username is invalid
                                    </Localized>
                                </div>
                            );
                            break;
                        case LoginError.ApiUserCannotLogin:
                            errorMessage = (
                                <div>
                                    <Localized id='login-error-api-user'>
                                        The user cannot be used with this application.
                                    </Localized>
                                </div>
                            );
                            break;
                        case LoginError.MfaEmailNotVerified:
                            errorMessage = (
                                <div>
                                    <Localized id='login-error-email-not-verified'>
                                        Your email address needs to be verified before you can log in. Please check your email for a verification link.
                                    </Localized>
                                </div>
                            );
                            break;
                        case LoginError.Unknown:
                            errorMessage = (
                                <div>
                                    <Localized id='login-error-unknown'>
                                        Unknown error
                                    </Localized>
                                </div>
                            );
                            break;
                        default:
                            errorMessage = (
                                <div>
                                    <Localized id='login-error-unknown'>
                                        Unknown error
                                    </Localized>
                                </div>
                            );
                            break;
                    }
                    setState({ errorMessage });
                } else if (loginResponse.LoggedIn && loginResponse.User != null) {
                    onLoggedIn(loginResponse.User);
                } else if (loginResponse.MfaRequired && (loginResponse.MfaToken ?? '').length > 0) {
                    onMfaRequired(loginResponse.MfaToken);
                } else {
                    setState({ errorMessage: (
                        <div>
                            <Localized id='login-error-unknown'>
                                Unknown error
                            </Localized>
                        </div>
                    ) });
                }
            }),
            catchError(error => {
                logger.error(error);
                setState({ errorMessage: (
                    <div>
                        <Localized id='login-error-unknown'>
                            Unknown error
                        </Localized>
                    </div>
                ), loading: false });
                return of();
            })
        ).subscribe();

        return () => {
            setState({ loading: false });
            subscription.unsubscribe();
        };

    }, [doLogIn]);

    return state.current;
}

export interface UseLogInState {
    errorMessage: React.ReactElement | undefined;
    loading: boolean;
    logIn: (userName: string, password: string) => void;
}
